import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useLiveSource } from "@/hooks/useLiveSource";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, Activity, ShieldCheck, AlertTriangle,
  User as UserIcon, Eye, Clock,
} from "lucide-react";

const fmtIST = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—";

const ACTION_CLR = {
  "toggle_feature_flag.update": "bg-amber-50 text-amber-700 border-amber-300",
  "manage_sub_admin.create": "bg-blue-50 text-blue-700 border-blue-300",
  "manage_sub_admin.delete": "bg-rose-50 text-rose-700 border-rose-300",
  "manage_role.create": "bg-blue-50 text-blue-700 border-blue-300",
  "reconcile_outbound_txn.create": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "approval_action.create": "bg-purple-50 text-purple-700 border-purple-300",
  "qa_api_test.create": "bg-cyan-50 text-cyan-700 border-cyan-300",
  "qa_browser_flow.create": "bg-cyan-50 text-cyan-700 border-cyan-300",
};
const colorFor = (action) => ACTION_CLR[action] || "bg-slate-50 text-slate-700 border-slate-300";

export default function AdminActivityLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [actor, setActor] = useState("all");
  const [onlySub, setOnlySub] = useState(false);
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [sinceHours, setSinceHours] = useState(168);

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { source } = useLiveSource();

  const load = async () => {
    setLoading(true);
    try {
      if (source === "live") {
        // Live-DB only supports flat field filters — most activity-log filters
        // map cleanly. since_hours / search are not honored here (db-read has
        // no timeframe / fulltext on this table).
        const filters = {};
        if (actor !== "all") filters.actor_id = actor;
        if (onlySub) filters.actor_is_sub_admin = true;
        if (method !== "all") filters.method = method;
        const { data } = await api.post("/admin/system-test/live-db", {
          table: "admin_activity_log",
          filters,
          sort_field: "created_at",
          sort_dir: -1,
          limit: 500,
        });
        setRows(data.rows || []);
        setTotal(data.rows?.length || 0);
        setStats(null);
        return;
      }
      const params = { since_hours: sinceHours, limit: 500 };
      if (actor !== "all") params.actor_id = actor;
      if (onlySub) params.only_sub_admins = true;
      if (status !== "all") params.status_bucket = status;
      if (method !== "all") params.method = method;
      if (action.trim()) params.action = action.trim();
      if (search.trim()) params.search = search.trim();
      const [list, st, ac] = await Promise.all([
        api.get("/admin/activity-log", { params }),
        api.get("/admin/activity-log/stats", { params: { since_hours: 24 } }),
        api.get("/admin/activity-log/actors", { params: { since_hours: 720 } }),
      ]);
      setRows(list.data.items || []);
      setTotal(list.data.total || 0);
      setStats(st.data);
      setActors(ac.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load activity log");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceHours, actor, onlySub, status, method, source]);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/admin/activity-log/${row.id}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const counts = useMemo(() => ({
    total,
    showing: rows.length,
    failed: rows.filter((r) => r.success === false).length,
    by_sub: rows.filter((r) => r.actor_is_sub_admin).length,
  }), [rows, total]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <span className="overline">Audit Trail</span>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="activity-log-title">Activity Log · Who did what</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every mutating action taken by admins and sub-admins. Captured automatically by middleware — no instrumentation needed in route handlers.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Last 24h" value={stats?.total ?? "—"} icon={Activity} accent="text-muted-foreground" />
          <Stat label="Sub-admin actions (24h)" value={stats?.sub_admin_actions ?? "—"} icon={ShieldCheck} accent="text-blue-700" />
          <Stat label="Failures (24h)" value={stats?.failed ?? "—"} icon={AlertTriangle} accent="text-rose-700" />
          <Stat label="Distinct actors (30d)" value={actors.length} icon={UserIcon} accent="text-emerald-700" />
        </div>

        {/* Filters */}
        <div className="surface-card p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-muted-foreground">Search summary / path / email</label>
            <div className="flex gap-1 mt-1">
              <Input value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} placeholder="email, path, target id…" data-testid="activity-search" />
              <Button variant="outline" onClick={load}><Search className="h-4 w-4" /></Button>
            </div>
          </div>
          <FilterSelect label="Actor" value={actor} setValue={setActor} testId="filter-actor"
            options={[["all","All actors"], ...actors.map((a)=>[a.actor_id || a.actor_email, `${a.actor_email}${a.actor_is_sub_admin ? " [SUB]" : ""}`])]} />
          <FilterSelect label="Status" value={status} setValue={setStatus} testId="filter-status"
            options={[["all","All"], ["success","Success"], ["failed","Failed"]]} />
          <FilterSelect label="Method" value={method} setValue={setMethod} testId="filter-method"
            options={[["all","All"], ["POST","POST"], ["PUT","PUT"], ["PATCH","PATCH"], ["DELETE","DELETE"]]} />
          <FilterSelect label="Window" value={String(sinceHours)} setValue={(v)=>setSinceHours(Number(v))} testId="filter-window"
            options={[["24","Last 24h"], ["72","3 days"], ["168","7 days"], ["720","30 days"]]} />
          <div className="flex items-center gap-2 pt-5">
            <label className="text-xs flex items-center gap-1.5 select-none">
              <input type="checkbox" checked={onlySub} onChange={(e)=>setOnlySub(e.target.checked)} data-testid="filter-only-sub" />
              Sub-admins only
            </label>
            <Button size="sm" variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
          </div>
        </div>

        <div className="surface-card overflow-x-auto">
          {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div> :
            rows.length === 0 ? <div className="p-8 text-center text-muted-foreground">No activity recorded in this window.</div> :
            <table className="w-full text-sm">
              <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Actor</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2 text-left">Path</th>
                  <th className="px-3 py-2">HTTP</th>
                  <th className="px-3 py-2 text-left">Summary</th>
                  <th className="px-3 py-2 text-right">IP / UA</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-accent/30 cursor-pointer" onClick={() => openDetail(r)} data-testid={`activity-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtIST(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-1">
                        <span>{r.actor_email}</span>
                        {r.actor_is_sub_admin && <Badge variant="outline" className="text-[8px] bg-blue-50 text-blue-700 border-blue-300">SUB</Badge>}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">{r.actor_role}</div>
                    </td>
                    <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${colorFor(r.action)}`}>{r.action}</Badge></td>
                    <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{r.method}</Badge></td>
                    <td className="px-3 py-2 text-xs font-mono truncate max-w-[280px]">{r.path}</td>
                    <td className="px-3 py-2 text-center text-xs font-mono">{r.status_code}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[300px]">{r.summary}</td>
                    <td className="px-3 py-2 text-right text-[10px] font-mono text-muted-foreground truncate max-w-[120px]">{r.ip || "—"}</td>
                    <td className="px-3 py-2"><Eye className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>

        <div className="text-xs text-muted-foreground">
          Showing {counts.showing} of {counts.total} events · {counts.by_sub} by sub-admins · {counts.failed} failed
        </div>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />Activity entry</DialogTitle>
            <DialogDescription>Full audit row with actor, target, body, and response details.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field k="When" v={fmtIST(detail.created_at)} />
                <Field k="Actor" v={<span>{detail.actor_email}{detail.actor_is_sub_admin && <Badge variant="outline" className="ml-1 text-[9px] bg-blue-50 text-blue-700 border-blue-300">SUB-ADMIN</Badge>}</span>} />
                <Field k="Role" v={<span className="font-mono">{detail.actor_role}</span>} />
                <Field k="Status" v={<Badge variant="outline" className={detail.success ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-rose-50 text-rose-700 border-rose-300"}>{detail.status_code}</Badge>} />
                <Field k="Method" v={detail.method} />
                <Field k="Action" v={<Badge variant="outline" className={`${colorFor(detail.action)}`}>{detail.action}</Badge>} />
                <Field k="Target" v={detail.target_type ? `${detail.target_type}${detail.target_id ? ` · ${detail.target_id}` : ""}` : "—"} />
                <Field k="Elapsed" v={<span><Clock className="h-3 w-3 inline mr-1" />{detail.elapsed_ms ?? "—"}ms</span>} />
                <Field k="IP" v={<span className="font-mono text-xs">{detail.ip || "—"}</span>} />
                <Field k="User agent" v={<span className="text-xs">{detail.user_agent || "—"}</span>} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Path</div>
                <div className="font-mono text-xs surface-card p-2 break-all">{detail.path}</div>
              </div>
              {detail.summary && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Summary</div>
                  <div className="surface-card p-2 text-sm">{detail.summary}</div>
                </div>
              )}
              {detail.request_body && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Request body</div>
                  <pre className="bg-muted rounded-md p-3 text-[10px] overflow-x-auto max-h-60 whitespace-pre-wrap">{JSON.stringify(detail.request_body, null, 2)}</pre>
                </div>
              )}
              {detail.response_summary && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Response summary</div>
                  <pre className="bg-muted rounded-md p-3 text-[10px] overflow-x-auto max-h-60 whitespace-pre-wrap">{JSON.stringify(detail.response_summary, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="surface-card p-4 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-2xl font-display font-bold mt-1 ${accent}`}>{value}</div>
      </div>
      <Icon className={`h-6 w-6 ${accent} opacity-60`} />
    </div>
  );
}

function FilterSelect({ label, value, setValue, options, testId }) {
  return (
    <div className="min-w-[140px]">
      <label className="text-xs text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="text-sm font-medium mt-0.5 break-all">{v}</div>
    </div>
  );
}
